import { DomainRuleError } from "./core";

export const clinicalEventTypes = [
  "diagnosis",
  "injection",
  "procedure",
  "test",
  "treatment",
  "course",
  "symptom",
  "medical_history",
] as const;

export type ClinicalEventType = (typeof clinicalEventTypes)[number];

export const clinicalEventImportances = ["normal", "high", "critical"] as const;
export type ClinicalEventImportance = (typeof clinicalEventImportances)[number];

export const clinicalTextColors = ["red", "blue", "green", "amber", "violet"] as const;
export type ClinicalTextColor = (typeof clinicalTextColors)[number];

export const clinicalTextSizes = ["small", "normal", "large", "xlarge"] as const;
export type ClinicalTextSize = (typeof clinicalTextSizes)[number];

export interface ClinicalTextRun {
  text: string;
  bold?: true;
  italic?: true;
  color?: ClinicalTextColor;
  size?: ClinicalTextSize;
  keyword?: true;
}

export interface ClinicalParagraph {
  type: "paragraph";
  runs: ClinicalTextRun[];
}

export interface ClinicalDocumentContent {
  schemaVersion: 1;
  blocks: ClinicalParagraph[];
}

const MAX_BLOCKS = 80;
const MAX_RUNS = 1_000;
const MAX_RUN_CHARACTERS = 4_000;
const MAX_DOCUMENT_CHARACTERS = 24_000;

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

export function isClinicalEventType(value: unknown): value is ClinicalEventType {
  return clinicalEventTypes.some((candidate) => candidate === value);
}

export function isClinicalEventImportance(
  value: unknown,
): value is ClinicalEventImportance {
  return clinicalEventImportances.some((candidate) => candidate === value);
}

export function isClinicalTextColor(value: unknown): value is ClinicalTextColor {
  return clinicalTextColors.some((candidate) => candidate === value);
}

export function isClinicalTextSize(value: unknown): value is ClinicalTextSize {
  return clinicalTextSizes.some((candidate) => candidate === value);
}

function sameFormatting(left: ClinicalTextRun, right: ClinicalTextRun): boolean {
  return (
    left.bold === right.bold &&
    left.italic === right.italic &&
    left.color === right.color &&
    left.size === right.size &&
    left.keyword === right.keyword
  );
}

function mergeAdjacentRuns(runs: ClinicalTextRun[]): ClinicalTextRun[] {
  const merged: ClinicalTextRun[] = [];
  for (const run of runs) {
    const previous = merged.at(-1);
    if (previous && sameFormatting(previous, run)) {
      previous.text += run.text;
    } else {
      merged.push({ ...run });
    }
  }
  return merged;
}

function parseRun(value: unknown): ClinicalTextRun {
  if (!isRecord(value) || typeof value.text !== "string") {
    throw new DomainRuleError("INVALID_CLINICAL_DOCUMENT", "임상 기록의 문장 형식을 확인하십시오.");
  }

  const text = value.text.replace(/\r\n?/g, "\n");
  if (text.length > MAX_RUN_CHARACTERS || text.includes("\u0000")) {
    throw new DomainRuleError("INVALID_CLINICAL_DOCUMENT", "임상 기록의 문장 길이를 확인하십시오.");
  }
  if (value.color !== undefined && !isClinicalTextColor(value.color)) {
    throw new DomainRuleError("INVALID_CLINICAL_DOCUMENT", "허용되지 않은 글자색입니다.");
  }
  if (value.size !== undefined && !isClinicalTextSize(value.size)) {
    throw new DomainRuleError("INVALID_CLINICAL_DOCUMENT", "허용되지 않은 글자 크기입니다.");
  }
  if (
    (value.bold !== undefined && typeof value.bold !== "boolean") ||
    (value.italic !== undefined && typeof value.italic !== "boolean") ||
    (value.keyword !== undefined && typeof value.keyword !== "boolean")
  ) {
    throw new DomainRuleError("INVALID_CLINICAL_DOCUMENT", "임상 기록의 서식 값을 확인하십시오.");
  }

  const run: ClinicalTextRun = { text };
  if (value.bold === true || value.keyword === true) run.bold = true;
  if (value.italic === true) run.italic = true;
  if (value.keyword === true) run.keyword = true;
  if (isClinicalTextColor(value.color)) run.color = value.color;
  if (isClinicalTextSize(value.size)) run.size = value.size;
  return run;
}

export function parseClinicalDocumentContent(value: unknown): ClinicalDocumentContent {
  if (
    !isRecord(value) ||
    value.schemaVersion !== 1 ||
    !Array.isArray(value.blocks) ||
    value.blocks.length === 0 ||
    value.blocks.length > MAX_BLOCKS
  ) {
    throw new DomainRuleError("INVALID_CLINICAL_DOCUMENT", "임상 기록 구조를 확인하십시오.");
  }

  let runCount = 0;
  let characterCount = 0;
  const blocks = value.blocks.map((rawBlock): ClinicalParagraph => {
    if (
      !isRecord(rawBlock) ||
      rawBlock.type !== "paragraph" ||
      !Array.isArray(rawBlock.runs) ||
      rawBlock.runs.length === 0
    ) {
      throw new DomainRuleError("INVALID_CLINICAL_DOCUMENT", "임상 기록 문단을 확인하십시오.");
    }

    const parsedRuns = rawBlock.runs.map(parseRun);
    runCount += parsedRuns.length;
    characterCount += parsedRuns.reduce((total, run) => total + run.text.length, 0);
    if (runCount > MAX_RUNS || characterCount > MAX_DOCUMENT_CHARACTERS) {
      throw new DomainRuleError("INVALID_CLINICAL_DOCUMENT", "임상 기록의 전체 길이를 줄이십시오.");
    }

    const runs = mergeAdjacentRuns(parsedRuns).filter((run) => run.text.length > 0);
    return { type: "paragraph", runs: runs.length > 0 ? runs : [{ text: "" }] };
  });

  return { schemaVersion: 1, blocks };
}

export function plainTextFromClinicalContent(content: ClinicalDocumentContent): string {
  return content.blocks
    .map((block) => block.runs.map((run) => run.text).join(""))
    .join("\n");
}
